import { createContext, useContext, ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface DialogProps {
  open: boolean
  onOpenChange?: (open: boolean) => void
  children: ReactNode
}

interface DialogContextValue {
  close: () => void
}

const DialogContext = createContext<DialogContextValue>({
  close: () => {},
})

function joinClasses(...classes: Array<string | undefined>) {
  return classes.filter((item) => Boolean(item && item.trim().length > 0)).join(' ')
}

export function Dialog({ open, onOpenChange, children }: DialogProps) {
  if (!open) return null

  const handleClose = () => onOpenChange?.(false)

  return createPortal(
    <DialogContext.Provider value={{ close: handleClose }}>
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div
          aria-hidden
          className="fixed inset-0 bg-black/40"
          onClick={handleClose}
        />
        <div className="relative z-10 w-full max-w-2xl" role="dialog" aria-modal="true">
          {children}
        </div>
      </div>
    </DialogContext.Provider>,
    document.body,
  )
}

export function DialogContent({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={joinClasses('w-full rounded-2xl bg-white p-6 shadow-2xl', className)}>
      {children}
    </div>
  )
}

export function DialogHeader({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={joinClasses('space-y-1.5', className)}>{children}</div>
}

export function DialogTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h2 className={joinClasses('text-lg font-semibold leading-none tracking-tight text-gray-900', className)}>
      {children}
    </h2>
  )
}

export function DialogFooter({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={joinClasses('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}>{children}</div>
}

export function useDialog() {
  return useContext(DialogContext)
}
